import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Avatar,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  Select,
  Option,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  IconButton,
  Card,
  Tabs,
  TabList,
  Tab,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBack,
  PencilSimple as Edit,
  Phone,
  Envelope as Mail,
  Warning,
  Plus as Add,
  ArrowsClockwise as Loop,
  Trash as Delete,
  ChatCircle as Sms,
  CaretLeft,
  CaretRight,
  Briefcase,
  Kanban,
} from '@phosphor-icons/react';
import type { ContactNote } from '../../../types/crm';
import {
  useContact,
  useUsers,
  useUpdateContactAssignment,
  useUpdateQualificationStatus,
  useDeleteContact,
  useContactRelatedJobs,
  useContactNeighbors,
} from '../../../hooks/useCrm';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import { AddNoteModal } from './AddNoteModal';
import { DripEnrollmentModal } from './DripEnrollmentModal';
import { useAuth } from '../../../contexts/AuthContext';
import { ContactTimeline, useAllContactNotes } from './ContactTimeline';
import { EditNoteModal } from './EditNoteModal';
import { DeleteNoteModal } from './DeleteNoteModal';
import { useContactOpportunities } from '../../../hooks/useOpportunities';
import { ContactForm, type SectionKey } from './ContactForm';
import { SmsConsentChip } from './SmsConsentChip';
import OpportunityModal from '../opportunities/OpportunityModal';
import { AddJobModal } from '../../jobs/AddJobModal';

interface ContactDetailProps {
  contactId?: number;
  onEdit?: () => void;
  onClose?: () => void;
  onDelete?: () => void;
}

// Qualification status options for dropdown
const qualificationOptions = [
  { value: 'not_set', label: 'Not set' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'disqualified', label: 'Disqualified' },
];

/** Small keyboard-hint chip (the "N" / "E" badges on action buttons). */
const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontSize: '10px',
      fontWeight: 700,
      lineHeight: 1,
      px: 0.5,
      py: 0.25,
      borderRadius: '4px',
      border: '1px solid',
      borderColor: 'currentColor',
      opacity: 0.65,
    }}
  >
    {children}
  </Box>
);

/** Uppercase micro-label used across the header meta strip and rail cards. */
const MicroLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    level="body-xs"
    sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.tertiary', fontWeight: 'lg' }}
  >
    {children}
  </Typography>
);

const isTypingTarget = (el: EventTarget | null) => {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
};

export const ContactDetail: React.FC<ContactDetailProps> = ({
  contactId: contactIdProp,
  onEdit,
  onClose,
}) => {
  const navigate = useNavigate();
  const params = useParams<{ contactId: string }>();
  const resolvedContactId = contactIdProp ?? parseInt(params.contactId ?? '0');
  const isStandalonePage = !onClose;

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInitialSection, setEditInitialSection] = useState<SectionKey | undefined>(undefined);
  const [assignmentMessage, setAssignmentMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [qualificationMessage, setQualificationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAddOpportunity, setShowAddOpportunity] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [activeComposer, setActiveComposer] = useState<'sms' | 'email' | null>(null);
  const [calling, setCalling] = useState(false);
  const [activityFilter, setActivityFilter] = useState<'all' | 'notes' | 'calls' | 'emails'>('all');
  // Reported by the timeline: notes + texts + calls + email threads under the
  // active filter, so the header count matches what is actually listed.
  const [activityCount, setActivityCount] = useState(0);

  const { user } = useAuth();

  const [showAddNote, setShowAddNote] = useState(false);
  const [showDrips, setShowDrips] = useState(false);
  const [showEditNote, setShowEditNote] = useState(false);
  const [showDeleteNote, setShowDeleteNote] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ContactNote | null>(null);

  const { data: contact, isLoading, error, refetch: refetchContact } = useContact(resolvedContactId);
  const { data: neighbors } = useContactNeighbors(resolvedContactId);
  const { data: opportunitiesRaw, isLoading: oppsLoading } = useContactOpportunities(resolvedContactId);
  const { data: relatedJobsRaw, isLoading: jobsLoading, refetch: refetchRelatedJobs } = useContactRelatedJobs(resolvedContactId);
  const opportunities: any[] = Array.isArray(opportunitiesRaw) ? opportunitiesRaw : (opportunitiesRaw as any)?.results ?? [];
  const relatedJobs: any[] = Array.isArray(relatedJobsRaw) ? relatedJobsRaw : (relatedJobsRaw as any)?.jobs ?? [];
  const { data: users = [] } = useUsers();
  const updateAssignmentMutation = useUpdateContactAssignment();
  const updateQualificationMutation = useUpdateQualificationStatus();
  const { data: notes = [], refetch: refetchNotes } = useAllContactNotes(resolvedContactId);
  const deleteContactMutation = useDeleteContact();

  const anyModalOpen =
    showDeleteDialog || showEditModal || showAddOpportunity || showAddJob || showAddNote ||
    showDrips || showEditNote || showDeleteNote;

  // Keyboard shortcuts: N adds a note, E opens edit (never while typing or
  // while a modal is open).
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (anyModalOpen || isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();
        setShowAddNote(true);
      } else if (event.key === 'e' || event.key === 'E') {
        event.preventDefault();
        setEditInitialSection(undefined);
        setShowEditModal(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [anyModalOpen]);

  const getInitials = (name: string) =>
    name.split(' ').filter(Boolean).map((part) => part[0]).slice(0, 2).join('').toUpperCase();

  const handleCall = async () => {
    if (!contact?.phone || calling) return;
    setCalling(true);
    try {
      await api.post('/api/inbox/call/start/', {
        to_number: contact.phone,
        lead_id: contact.id,
      });
      toast.success(
        `Calling your phone now — answer to be connected to ${contact.first_name || 'the contact'}.`,
        { duration: 8000 }
      );
    } catch (err: any) {
      toast.error(err?.responseData?.error || 'Failed to start the call.');
    } finally {
      setCalling(false);
    }
  };

  const handleDeleteContact = async () => {
    try {
      await deleteContactMutation.mutateAsync(resolvedContactId);
      setShowDeleteDialog(false);
      if (isStandalonePage) navigate('/crm/contacts');
      else onClose?.();
    } catch {
      // mutation exposes its own error state
    }
  };

  const handleEdit = (section?: SectionKey) => {
    if (onEdit) onEdit();
    else {
      setEditInitialSection(section);
      setShowEditModal(true);
    }
  };

  const handleAssignmentChange = async (assignedTo: number | null) => {
    setAssignmentMessage(null);
    const assignedUser = assignedTo ? users.find((u: any) => u.id === assignedTo) : null;
    try {
      await updateAssignmentMutation.mutateAsync({ contactId: resolvedContactId, assignedTo });
      setAssignmentMessage({
        type: 'success',
        text: assignedUser ? `Assigned to ${assignedUser.full_name}` : 'Unassigned',
      });
      setTimeout(() => setAssignmentMessage(null), 3000);
    } catch {
      setAssignmentMessage({ type: 'error', text: 'Failed to update assignment' });
    }
  };

  const handleQualificationChange = async (qualificationStatus: string) => {
    setQualificationMessage(null);
    const statusConfig = qualificationOptions.find((option) => option.value === qualificationStatus);
    try {
      await updateQualificationMutation.mutateAsync({ contactId: resolvedContactId, qualificationStatus });
      setQualificationMessage({ type: 'success', text: `Marked ${statusConfig?.label || qualificationStatus}` });
      setTimeout(() => setQualificationMessage(null), 3000);
    } catch {
      setQualificationMessage({ type: 'error', text: 'Failed to update qualification' });
    }
  };

  const handleEditNote = (note: ContactNote) => {
    setSelectedNote(note);
    setShowEditNote(true);
  };

  const handleDeleteNote = (noteId: number) => {
    const note = notes.find((n) => n.id === noteId);
    if (note) {
      setSelectedNote(note);
      setShowDeleteNote(true);
    }
  };

  const handleNoteModalClose = () => {
    setShowAddNote(false);
    setShowEditNote(false);
    setShowDeleteNote(false);
    setSelectedNote(null);
    refetchNotes();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contact) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger" sx={{ mb: 2 }}>Contact not found.</Alert>
        <Button variant="outlined" color="primary" startDecorator={<ArrowBack />} onClick={() => navigate('/crm/contacts')}>
          Back to contacts
        </Button>
      </Box>
    );
  }

  const fullName = contact.full_name || `${contact.first_name} ${contact.last_name}`.trim();
  const assignedToId =
    (typeof contact.assigned_to === 'object' && contact.assigned_to !== null
      ? (contact.assigned_to as any).id
      : contact.assigned_to) || null;
  const ownerName =
    (typeof contact.assigned_to === 'object' && contact.assigned_to !== null
      ? (contact.assigned_to as any).name
      : users.find((u: any) => u.id === assignedToId)?.full_name) || 'Unassigned';
  const qualificationLabel =
    qualificationOptions.find((o) => o.value === contact.qualification_status)?.label || 'Not set';
  const primaryAddress = contact.addresses?.find((addr: any) => addr.is_primary) || contact.addresses?.[0];
  const prefers = contact.preferred_contact_method
    ? contact.preferred_contact_method.charAt(0).toUpperCase() + contact.preferred_contact_method.slice(1)
    : null;

  const beginningOfHistory = (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'neutral.outlinedBorder',
        borderRadius: 'md',
        textAlign: 'center',
        px: 2,
        py: 3,
        mt: 2,
      }}
    >
      <Typography level="title-sm">Beginning of history</Typography>
      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
        Nothing before this.{' '}
        <Link component="button" onClick={() => setShowAddNote(true)}>Log the first call</Link>{' '}
        and it lands at the top.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: 'background.body' }}>
      {/* ------------------------------------------------ top utility bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 2, md: 3 },
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
          flexWrap: 'wrap',
        }}
      >
        {isStandalonePage && (
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            startDecorator={<ArrowBack />}
            onClick={() => navigate('/crm/contacts')}
          >
            Back to contacts
          </Button>
        )}
        <Typography level="title-lg" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fullName}
        </Typography>
        {neighbors && neighbors.position != null && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              border: '1px solid', borderColor: 'divider', borderRadius: 'md', px: 0.5, py: 0.25,
            }}
          >
            <IconButton
              size="sm" variant="plain" color="neutral"
              disabled={!neighbors.prev_id}
              onClick={() => neighbors.prev_id && navigate(`/crm/contacts/${neighbors.prev_id}`)}
              aria-label="Previous contact"
            >
              <CaretLeft />
            </IconButton>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {neighbors.position} / {neighbors.total}
            </Typography>
            <IconButton
              size="sm" variant="plain" color="neutral"
              disabled={!neighbors.next_id}
              onClick={() => neighbors.next_id && navigate(`/crm/contacts/${neighbors.next_id}`)}
              aria-label="Next contact"
            >
              <CaretRight />
            </IconButton>
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined" color="primary" size="sm"
            startDecorator={<Edit />} endDecorator={<Kbd>E</Kbd>}
            onClick={() => handleEdit()}
          >
            Edit
          </Button>
          <Button
            variant="outlined" size="sm" color="danger"
            startDecorator={<Delete />}
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete
          </Button>
          {onClose && (
            <Button variant="outlined" size="sm" color="primary" onClick={onClose}>✕</Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ------------------------------------------------ header card */}
        <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, pt: 2, pb: 1.5, flexWrap: 'wrap' }}>
            <Avatar size="lg">{getInitials(fullName)}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography level="title-lg">{fullName}</Typography>
              {(contact.title || contact.company_name) && (
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  {contact.title}
                  {contact.title && contact.company_name && ' · '}
                  {contact.company_name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="sm" variant="soft" color={
                ({ lead: 'primary', prospect: 'warning', customer: 'success' } as any)[contact.contact_type] || 'neutral'
              }>
                {contact.contact_type?.replace('_', ' ')}
              </Chip>
              {contact.tags?.map((tag: any) => (
                <Chip key={tag.id} size="sm" variant="solid" sx={{ backgroundColor: tag.color, color: '#fff' }}>
                  {tag.name}
                </Chip>
              ))}
              <Chip
                size="sm"
                variant="outlined"
                color="neutral"
                onClick={() => handleEdit('tags')}
                sx={{ borderStyle: 'dashed', cursor: 'pointer' }}
                startDecorator={<Add size={12} />}
              >
                Tag
              </Chip>
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 },
              px: 2.5, py: 1.25, flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicroLabel>Last contact</MicroLabel>
              <Typography level="body-sm" fontWeight="lg" startDecorator={
                <Box component="span" sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: contact.last_contacted ? 'success.400' : 'warning.400',
                }} />
              }>
                {contact.last_contacted ? new Date(contact.last_contacted).toLocaleDateString() : 'Never'}
              </Typography>
            </Box>
            <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicroLabel>Owner</MicroLabel>
              <Typography level="body-sm" fontWeight="lg">{ownerName}</Typography>
            </Box>
            <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicroLabel>Qualification</MicroLabel>
              <Typography level="body-sm" fontWeight="lg">{qualificationLabel}</Typography>
            </Box>
            {contact.phone && (
              <>
                <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MicroLabel>Texting</MicroLabel>
                  <SmsConsentChip contact={contact} onRecorded={refetchContact} />
                </Box>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                variant="solid" size="sm"
                startDecorator={<Add />} endDecorator={<Kbd>N</Kbd>}
                onClick={() => setShowAddNote(true)}
              >
                Note
              </Button>
              <Button
                variant="outlined" color="primary" size="sm" startDecorator={<Phone />}
                onClick={handleCall}
                loading={calling}
                disabled={!contact.phone}
              >
                Call
              </Button>
              <Button
                variant={activeComposer === 'email' ? 'solid' : 'outlined'}
                color="primary" size="sm" startDecorator={<Mail />}
                onClick={() => setActiveComposer(activeComposer === 'email' ? null : 'email')}
                disabled={!contact.email}
              >
                Email
              </Button>
              <Button
                variant={activeComposer === 'sms' ? 'solid' : 'outlined'}
                color="primary" size="sm" startDecorator={<Sms />}
                onClick={() => setActiveComposer(activeComposer === 'sms' ? null : 'sms')}
                disabled={!contact.phone}
              >
                SMS
              </Button>
              {user?.is_organization_admin && (
                <Button
                  variant="outlined" color="primary" size="sm" startDecorator={<Loop />}
                  onClick={() => setShowDrips(true)}
                >
                  Drip
                </Button>
              )}
            </Stack>
          </Box>
        </Card>

        {/* ------------------------------------------------ body grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {/* Activity feed */}
          <Card variant="outlined">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography level="title-md">
                Activity{' '}
                {activityCount > 0 && (
                  <Typography component="span" level="body-sm" sx={{ color: 'text.tertiary' }}>
                    {activityCount}
                  </Typography>
                )}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Tabs
                size="sm"
                value={activityFilter}
                onChange={(_, value) => setActivityFilter(value as typeof activityFilter)}
                sx={{ bgcolor: 'transparent' }}
              >
                <TabList disableUnderline sx={{ gap: 0.5, bgcolor: 'background.level1', borderRadius: 'md', p: 0.5 }}>
                  <Tab value="all" disableIndicator>All</Tab>
                  <Tab value="notes" disableIndicator>Notes</Tab>
                  <Tab value="calls" disableIndicator>Calls</Tab>
                  <Tab value="emails" disableIndicator>Email</Tab>
                </TabList>
              </Tabs>
            </Box>

            {/* Inline note trigger */}
            <Box
              onClick={() => setShowAddNote(true)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                border: '1px solid', borderColor: 'neutral.outlinedBorder',
                borderRadius: 'md', px: 1.5, py: 1.25, cursor: 'text',
                '&:hover': { borderColor: 'primary.300', bgcolor: 'background.level1' },
              }}
            >
              <Avatar size="sm">
                {getInitials(
                  [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'Me'
                )}
              </Avatar>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Add a note about {contact.first_name || fullName}…
              </Typography>
            </Box>

            <ContactTimeline
              contactId={resolvedContactId}
              phoneNumber={contact.phone}
              emailAddress={contact.email}
              leadName={fullName}
              activeComposer={activeComposer}
              onAddNote={() => setShowAddNote(true)}
              onEditNote={handleEditNote}
              onDeleteNote={handleDeleteNote}
              onCloseComposer={() => setActiveComposer(null)}
              typeFilter={activityFilter}
              onCountChange={setActivityCount}
              footer={beginningOfHistory}
            />
          </Card>

          {/* Right rail */}
          <Stack spacing={2}>
            <Card variant="outlined">
              <MicroLabel>Reach</MicroLabel>
              <Stack spacing={1.25} sx={{ mt: 0.5 }}>
                {contact.email && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Email</Typography>
                    <Link level="body-sm" href={`mailto:${contact.email}`} sx={{ wordBreak: 'break-all' }}>
                      {contact.email}
                    </Link>
                  </Box>
                )}
                {contact.phone && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Phone</Typography>
                    <Link level="body-sm" href={`tel:${contact.phone}`}>{contact.phone}</Link>
                  </Box>
                )}
                {primaryAddress && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Address</Typography>
                    <Link
                      level="body-sm"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        [primaryAddress.street_address_1, primaryAddress.city, primaryAddress.state_province, primaryAddress.postal_code].filter(Boolean).join(', ')
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block' }}
                    >
                      {primaryAddress.street_address_1}
                      {primaryAddress.street_address_2 ? `, ${primaryAddress.street_address_2}` : ''}
                      <br />
                      {[primaryAddress.city, primaryAddress.state_province].filter(Boolean).join(', ')}{' '}
                      {primaryAddress.postal_code}
                    </Link>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {[primaryAddress.address_type, primaryAddress.country]
                        .filter(Boolean)
                        .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
                        .join(' · ')}
                    </Typography>
                  </Box>
                )}
                {prefers && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Prefers</Typography>
                    <Typography level="body-sm" fontWeight="lg">{prefers}</Typography>
                  </Box>
                )}
              </Stack>
            </Card>

            <Card variant="outlined">
              <MicroLabel>Ownership</MicroLabel>
              <Stack spacing={1.25} sx={{ mt: 0.5 }}>
                <Box>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>Assigned to</Typography>
                  {/* These selects save on change. MUI also fires onChange (with no
                      event) to "correct" a value missing from the options — e.g. the
                      assignee before the users list loads — which used to PATCH the
                      contact to Unassigned on page load. Only act on real picks. */}
                  <Select
                    value={assignedToId}
                    onChange={(event, value) => { if (event) handleAssignmentChange(value as number | null); }}
                    size="sm"
                    placeholder="Unassigned"
                    disabled={updateAssignmentMutation.isPending}
                  >
                    <Option value={null}>Unassigned</Option>
                    {users.map((u: any) => (
                      <Option key={u.id} value={u.id}>{u.full_name}</Option>
                    ))}
                  </Select>
                  {assignmentMessage && (
                    <Alert color={assignmentMessage.type === 'success' ? 'success' : 'danger'} size="sm" sx={{ mt: 0.5 }}>
                      {assignmentMessage.text}
                    </Alert>
                  )}
                </Box>
                <Box>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>Qualification</Typography>
                  <Select
                    value={contact.qualification_status}
                    onChange={(event, value) => { if (event) handleQualificationChange(value as string); }}
                    size="sm"
                    disabled={updateQualificationMutation.isPending}
                  >
                    {qualificationOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                  {qualificationMessage && (
                    <Alert color={qualificationMessage.type === 'success' ? 'success' : 'danger'} size="sm" sx={{ mt: 0.5 }}>
                      {qualificationMessage.text}
                    </Alert>
                  )}
                </Box>
                {contact.source && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Source</Typography>
                    <Typography level="body-sm" fontWeight="lg">
                      {(contact.source as any)?.name || contact.source}
                      {contact.referred_by_name ? ` · ${contact.referred_by_name}` : ''}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>

            <Card variant="outlined">
              <MicroLabel>Pipeline</MicroLabel>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    border: '1px solid', borderColor: 'neutral.outlinedBorder',
                    borderRadius: 'md', px: 1.5, py: 1,
                  }}
                >
                  <Kanban size={16} />
                  <Typography level="body-sm" fontWeight="lg" sx={{ flex: 1 }}>Opportunities</Typography>
                  {oppsLoading ? <CircularProgress size="sm" /> : (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{opportunities.length}</Typography>
                  )}
                  <IconButton size="sm" variant="plain" color="primary" onClick={() => setShowAddOpportunity(true)} aria-label="Add opportunity">
                    <Add />
                  </IconButton>
                </Box>
                {opportunities.slice(0, 4).map((opp: any) => (
                  <Box
                    key={opp.id}
                    onClick={() => navigate(`/crm/opportunities/${opp.id}`)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 'sm', '&:hover': { bgcolor: 'background.level1' } }}
                  >
                    <Typography level="body-xs" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {opp.title || opp.name}
                    </Typography>
                    {opp.value != null && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        ${Number(opp.value).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                ))}

                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    border: '1px solid', borderColor: 'neutral.outlinedBorder',
                    borderRadius: 'md', px: 1.5, py: 1,
                  }}
                >
                  <Briefcase size={16} />
                  <Typography level="body-sm" fontWeight="lg" sx={{ flex: 1 }}>Related jobs</Typography>
                  {jobsLoading ? <CircularProgress size="sm" /> : (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{relatedJobs.length}</Typography>
                  )}
                  <IconButton size="sm" variant="plain" color="primary" onClick={() => setShowAddJob(true)} aria-label="Add job">
                    <Add />
                  </IconButton>
                </Box>
                {relatedJobs.slice(0, 4).map((job: any) => (
                  <Box
                    key={job.id}
                    onClick={() => { window.location.href = `/operations/jobs/${job.id}`; }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 'sm', '&:hover': { bgcolor: 'background.level1' } }}
                  >
                    <Chip size="sm" variant="soft" sx={{ flexShrink: 0, fontSize: '10px' }}>{job.status}</Chip>
                    <Typography level="body-xs" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.address || job.site_address || job.title || `Job #${job.id}`}
                    </Typography>
                  </Box>
                ))}

                {!oppsLoading && !jobsLoading && opportunities.length === 0 && relatedJobs.length === 0 && (
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Nothing open. Create an opportunity to pull this contact into forecasting.
                  </Typography>
                )}
              </Stack>
            </Card>
          </Stack>
        </Box>
      </Box>

      {/* Add Opportunity Modal */}
      <OpportunityModal
        open={showAddOpportunity}
        onClose={() => setShowAddOpportunity(false)}
        contactId={resolvedContactId}
      />

      <AddJobModal
        open={showAddJob}
        onClose={() => setShowAddJob(false)}
        onSuccess={() => refetchRelatedJobs()}
        defaultContactId={resolvedContactId}
      />

      {/* Note Modals */}
      <AddNoteModal open={showAddNote} onClose={handleNoteModalClose} contactId={resolvedContactId} />

      <DripEnrollmentModal
        open={showDrips}
        onClose={() => setShowDrips(false)}
        contactId={resolvedContactId}
        contactName={fullName}
      />

      {selectedNote && (
        <>
          <EditNoteModal open={showEditNote} onClose={handleNoteModalClose} note={selectedNote} />
          <DeleteNoteModal open={showDeleteNote} onClose={handleNoteModalClose} note={selectedNote} />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Modal open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <ModalDialog size="sm">
          <DialogTitle>
            <Warning color="var(--joy-palette-danger-500)" style={{ marginRight: 8 }} />
            Delete Contact
          </DialogTitle>
          <DialogContent>
            Are you sure you want to delete this contact?
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="solid" color="danger" onClick={handleDeleteContact} loading={deleteContactMutation.isPending}>
              Delete
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Edit Contact Modal (standalone page mode) */}
      {isStandalonePage && (
        <Modal open={showEditModal} onClose={() => { setShowEditModal(false); setEditInitialSection(undefined); }}>
          <ModalDialog size="lg" sx={{ maxWidth: '1000px', width: '94vw', maxHeight: '92vh', overflow: 'hidden', p: 0 }}>
            <ContactForm
              contactId={resolvedContactId}
              initialSection={editInitialSection}
              onSave={() => { setShowEditModal(false); setEditInitialSection(undefined); }}
              onCancel={() => { setShowEditModal(false); setEditInitialSection(undefined); }}
            />
          </ModalDialog>
        </Modal>
      )}
    </Box>
  );
};